import { useMemo, useState } from 'react';
import { Bell, CheckCheck, Clock3, Inbox, X } from 'lucide-react';
import {
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@api/hooks/useNotifications';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import type { NotificationRecord } from '@schemas/api';
import { type DocumentSummaryPayload, formatDeadlineBucket, formatNotificationTimestamp } from '@utils/notifications';
import { formatDate } from '@utils/formatters';

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
  { value: 'dismissed', label: 'Dismissed' },
];

const categoryOptions = [
  { value: 'all', label: 'All categories' },
  { value: 'approval_reminder', label: 'Approval reminders' },
  { value: 'document_summary', label: 'Document summaries' },
];

const renderApprovalReminder = (notification: NotificationRecord) => {
  const payload = notification.payload ?? {};
  const entityLabel = payload.document_type && payload.document_id
    ? `${payload.document_type} ${payload.document_id}`
    : payload.entity_type && payload.entity_id
    ? `${payload.entity_type} ${payload.entity_id}`
    : 'Pending approval item';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
        <Clock3 className="h-4 w-4" />
        Step {payload.step_number ?? '?'} reminder at hour {payload.reminder_hour ?? '?'}
      </div>
      <p className="mt-2 text-sm text-amber-900 dark:text-amber-100">{entityLabel}</p>
      {payload.action_type && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          Expected action: {payload.action_type}
        </p>
      )}
    </div>
  );
};

const renderDocumentSummary = (notification: NotificationRecord) => {
  const payload = (notification.payload ?? {}) as DocumentSummaryPayload;
  const items = payload.items ?? [];
  const counts = Object.entries(payload.counts ?? {});
  const scopeLabel = payload.summary_scope?.type === 'org_unit' ? 'Org unit scope' : 'Tenant scope';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
        <span className="rounded-full bg-primary-100 px-2 py-1 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
          {scopeLabel}
        </span>
        {payload.generated_for_date ? <span>Generated for {payload.generated_for_date}</span> : null}
        {payload.timezone ? <span>{payload.timezone}</span> : null}
      </div>

      {counts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {counts.map(([bucket, count]) => (
            <span
              key={bucket}
              className="rounded-full border border-border-default px-2.5 py-1 text-xs font-medium text-content-secondary"
            >
              {bucket.replace(/_/g, ' ')}: {count}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {items.slice(0, 6).map((item, index) => (
          <div key={`${notification.id}-${item.invoice_id ?? item.ap_posting_id ?? index}`} className="rounded-lg border border-border-default p-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="font-medium text-content-primary">
                  {item.invoice_number || 'Unnumbered invoice'}
                  {item.vendor_name ? ` • ${item.vendor_name}` : ''}
                </p>
                <p className="mt-1 text-sm text-content-secondary">
                  {formatDeadlineBucket(item.bucket, item.days_until)}
                  {item.deadline_at ? ` • ${formatDate(item.deadline_at)}` : ''}
                  {item.org_unit_name ? ` • ${item.org_unit_name}` : ''}
                </p>
              </div>
              {typeof item.amount === 'number' ? (
                <p className="text-sm font-semibold text-content-primary">
                  ${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-content-muted">
              {item.invoice_status ? <span>Invoice: {item.invoice_status}</span> : null}
              {item.invoice_match_status ? <span>Match: {item.invoice_match_status}</span> : null}
              {item.ap_posting_status ? <span>AP posting: {item.ap_posting_status}</span> : null}
              {item.ap_posting_approval_status ? <span>Approval: {item.ap_posting_approval_status}</span> : null}
            </div>
          </div>
        ))}
      </div>

      {items.length > 6 && (
        <p className="text-sm text-content-secondary">
          {items.length - 6} more item{items.length - 6 === 1 ? '' : 's'} in this summary.
        </p>
      )}
    </div>
  );
};

const renderNotificationBody = (notification: NotificationRecord) => {
  if (notification.category === 'document_summary') {
    return renderDocumentSummary(notification);
  }

  if (notification.category === 'approval_reminder') {
    return renderApprovalReminder(notification);
  }

  return notification.body ? (
    <p className="text-sm text-content-secondary">{notification.body}</p>
  ) : null;
};

const renderDeliveryStatus = (notification: NotificationRecord) => {
  const deliveries = notification.deliveries ?? [];

  if (deliveries.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-content-muted">Delivery</p>
      <div className="flex flex-wrap gap-2">
        {deliveries.map((delivery) => (
          <div
            key={delivery.id}
            className={`rounded-lg border px-3 py-2 text-xs ${
              delivery.delivery_status === 'failed'
                ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
                : 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
            }`}
          >
            <p className="font-medium">
              {delivery.channel.replace(/_/g, ' ')} • {delivery.delivery_status}
            </p>
            <p className="mt-1">
              Attempted {formatNotificationTimestamp(delivery.attempted_at)}
            </p>
            {delivery.error_message ? <p className="mt-1">{delivery.error_message}</p> : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export const NotificationsPage = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const queryOptions = useMemo(() => ({
    status: statusFilter === 'all' ? undefined : statusFilter,
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    limit: 100,
  }), [categoryFilter, statusFilter]);

  const { data, isLoading, error } = useNotifications(queryOptions);
  const markRead = useMarkNotificationRead();
  const dismissNotification = useDismissNotification();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.items ?? [];
  const unreadCount = notifications.filter((notification) => notification.status === 'unread').length;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
        Failed to load notifications.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-content-primary">Notifications</h1>
          <p className="text-content-secondary">
            Durable approval reminders and document deadline summaries delivered by the backend.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => markAllRead.mutate()}
          loading={markAllRead.isPending}
          className="inline-flex items-center gap-2"
        >
          <CheckCheck className="h-4 w-4" />
          Mark all read
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Inbox</CardTitle>
              <CardDescription>
                {data?.total ?? 0} notification{(data?.total ?? 0) === 1 ? '' : 's'} in scope.
              </CardDescription>
            </div>
            <div className="rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              {unreadCount} unread in current filter
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="mb-2 text-sm font-medium text-content-secondary">Status</p>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${statusFilter === option.value ? 'bg-primary-500 text-white' : 'bg-surface-raised text-content-secondary hover:text-content-primary'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-content-secondary">Category</p>
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setCategoryFilter(option.value)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${categoryFilter === option.value ? 'bg-primary-500 text-white' : 'bg-surface-raised text-content-secondary hover:text-content-primary'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {notifications.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-default p-10 text-center">
              <Inbox className="mx-auto h-10 w-10 text-content-muted" />
              <p className="mt-3 font-medium text-content-primary">No notifications in this view.</p>
              <p className="mt-1 text-sm text-content-secondary">
                Approval reminders and deadline summaries will appear here as the backend delivers them.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`rounded-xl border p-5 ${notification.status === 'unread' ? 'border-primary-300 bg-primary-50/40 dark:border-primary-700 dark:bg-primary-900/10' : 'border-border-default bg-surface-base'}`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2 text-content-primary">
                          <Bell className="h-4 w-4" />
                          <h2 className="text-lg font-semibold">{notification.title}</h2>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${notification.status === 'unread' ? 'bg-primary-500 text-white' : notification.status === 'dismissed' ? 'bg-gray-300 text-gray-700 dark:bg-gray-800 dark:text-gray-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'}`}>
                          {notification.status}
                        </span>
                        <span className="rounded-full border border-border-default px-2 py-0.5 text-xs font-medium text-content-secondary">
                          {notification.category.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-content-muted">{formatNotificationTimestamp(notification.created_at)}</p>
                      {notification.body && notification.category !== 'approval_reminder' && notification.category !== 'document_summary' ? (
                        <p className="mt-3 text-sm text-content-secondary">{notification.body}</p>
                      ) : null}
                      <div className="mt-4">
                        {renderNotificationBody(notification)}
                      </div>
                      {renderDeliveryStatus(notification)}
                    </div>
                    <div className="flex gap-2 lg:flex-col lg:items-end">
                      {notification.status === 'unread' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => markRead.mutate(notification.id)}
                          loading={markRead.isPending}
                        >
                          Mark read
                        </Button>
                      )}
                      {notification.status !== 'dismissed' && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => dismissNotification.mutate(notification.id)}
                          loading={dismissNotification.isPending}
                          className="inline-flex items-center gap-2"
                        >
                          <X className="h-4 w-4" />
                          Dismiss
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
